import { formatDateShortPreserveNull, toLocaleDatePreserveNull } from '@glow/common'
import { fallbackLanguage, userLanguage } from '@glow/common/utils/language'
import i18next from 'i18next'
import { Map } from 'immutable'
import padStart from 'lodash-es/padStart'
import toString from 'lodash-es/toString'
import { DateTime, Duration, Interval } from 'luxon'
import { ISODateString, ISODateTimeString, ISOTimeString } from '../types/coreEntitiesTypes'

export const zoneUTC = 'utc'

export function isValidDate(date: any) {
  return (DateTime.isDateTime(date) && date.isValid) || (date && DateTime.fromISO(date).isValid)
}

/**
 * Today's date as DateTime using default locale and setting all time fields to 0
 */
export function today() {
  return DateTime.local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
}

export function isSameDayLuxon(date1: string | DateTime, date2: string | DateTime) {
  const d1 = toLocaleDate(date1)
  const d2 = toLocaleDate(date2)
  return d1.hasSame(d2, 'day')
}

export function isInThePast(date: string | null) {
  if (!date) return false
  const duration = toLocaleDate(date).diffNow()
  return duration.milliseconds < 0
}

export function isDateBetweenIntervalLuxon(fromDate: DateTime, consignmentDate: DateTime, toDate: DateTime) {
  return Interval.fromDateTimes(fromDate.startOf('day'), toDate.plus({ days: 1 }).startOf('day')).contains(
    consignmentDate
  )
}

export function isFirstDateBeforeOrEqualToSecondDateLuxon(firstDate: DateTime, secondDate: DateTime) {
  return firstDate.startOf('day').valueOf() <= secondDate.startOf('day').valueOf()
}

export function getDurationInDaysLuxon(firstDate: DateTime, secondDate: DateTime) {
  return Math.floor(Interval.fromDateTimes(firstDate, secondDate).toDuration('days').days)
}

export function getTimePeriodDateTime(early: DateTime, late: DateTime) {
  return `${early.toFormat('D: HH:mm')} - ${format24HourTime(late)}`
}

export function getTimePeriodTimeFromString(earliest: string, latest: string) {
  const early = toLocaleDate(earliest)
  const late = toLocaleDate(latest)
  return getTimePeriodTime(early, late)
}

/**
 * Whether a time window ends on a later calendar day than it starts. The comparison is against the
 * window's own start, so the answer never depends on anything outside the two values shown.
 */
export function endsOnLaterDay(
  early: DateTime | string | null | undefined,
  late: DateTime | string | null | undefined
): boolean {
  if (!early || !late) return false
  const start = toLocaleDate(early)
  const end = toLocaleDate(late)
  return start.isValid && end.isValid && end.startOf('day') > start.startOf('day')
}

/** Locale-formatted time, null safe. Renders 19.05 in da/fi where hourMinute renders 19:05. */
export function localeTime(value: DateTime | string | null | undefined): string {
  return value ? format24HourTime(toLocaleDate(value)) : ''
}

/**
 * A time window, marked when it ends on a later day than it starts.
 *
 * [formatTime] is explicit because the two time formats in this file are not interchangeable:
 * hourMinute forces en-US so it always renders HH:mm, while localeTime follows the locale and
 * renders 19.05 in Danish and Finnish. Callers must keep whichever they already used.
 */
export function timeWindowString(
  early: DateTime | string | null | undefined,
  late: DateTime | string | null | undefined,
  formatTime: (value: DateTime | string | null | undefined) => string = hourMinute
): string {
  const window = `${formatTime(early)} - ${formatTime(late)}`
  return endsOnLaterDay(early, late) ? markedAsNextDay(window) : window
}

export function getTimePeriodTime(early: DateTime, late: DateTime) {
  return timeWindowString(early, late, localeTime)
}

/** Compact marker for a window ending the day after it starts. */
export const nextDayMarker = '+1'

/**
 * Joined with a non-breaking space: the ETA and ETD columns are narrow enough that a normal space
 * lets the marker wrap onto its own line, away from the time it belongs to.
 */
function markedAsNextDay(formatted: string): string {
  return `${formatted}\u00a0${nextDayMarker}`
}

/**
 * The day of the earliest valid instant given, or undefined when there is none. Used as the
 * reference a route's stop times are marked against.
 */
export function earliestDay(instants: Array<DateTime | null | undefined>): DateTime | undefined {
  return instants
    .filter((instant): instant is DateTime => !!instant?.isValid)
    .reduce<DateTime | undefined>(
      (earliest, instant) => (!earliest || instant < earliest ? instant : earliest),
      undefined
    )
    ?.startOf('day')
}

/**
 * Marks an already formatted time when the instant it represents falls on a later day than
 * [startDay]. Used in route stop lists, where the route start is the reference for every row, so a
 * stop after midnight is marked whether or not its own window happens to straddle it.
 */
export function withDayMarker(
  formatted: string,
  at: DateTime | string | null | undefined,
  startDay: DateTime | undefined
): string {
  if (!formatted || !at || !startDay) return formatted
  const instant = toLocaleDate(at)
  return instant.isValid && instant.startOf('day') > startDay ? markedAsNextDay(formatted) : formatted
}

export function calculateTimeDiff(dateStr1: string, dateStr2: string) {
  const date1 = toLocaleDate(dateStr1)
  const date2 = toLocaleDate(dateStr2)
  return date2.diff(date1)
}

export function formatDuration(diff: Duration) {
  return diff.toISOTime({ suppressMilliseconds: true })
}

export function getTimePeriod(earliest: string, latest: string) {
  const early = toLocaleDate(earliest)
  const late = toLocaleDate(latest)

  return earliest && latest && getTimePeriodDateTime(early, late)
}

export function toLocaleDate(date: string | DateTime | null | undefined): DateTime {
  if (DateTime.isDateTime(date)) return date
  const dateTime = typeof date === 'string' ? DateTime.fromISO(date) : DateTime.local()
  return dateTime.isValid ? dateTime : DateTime.local()
}

export function dateShort(date: DateTime | null) {
  if (!date) return ''
  return date.toLocaleString(DateTime.DATE_SHORT)
}

export function format24HourTime(dateTime: DateTime) {
  return dateTime.toLocaleString(DateTime.TIME_24_SIMPLE)
}

export const dateTimeRendererLocalized = (value: DateTime | string | null | undefined) => {
  if (!value) return ''
  const a = toLocaleDatePreserveNull(value)
  return a ? `${dateShort(a)} ${format24HourTimeWithSeconds(a)}` : ''
}

export function formatAsLocalDate(date: DateTime) {
  return date.toFormat("yyyy-MM-dd'T'HH:mm:ss")
}

export function hourMinute(dateTime: string | DateTime | null | undefined) {
  if (!dateTime) return ''
  return hourMinuteWithDateTime(toLocaleDate(dateTime))
}

export function hourMinuteWithDateTime(dateTime: DateTime) {
  if (!dateTime) return ''
  // We've been getting some errors in Sentry (GLOW-16E) about invalid locales
  // This forces a locale, thus avoiding the detection code that fails
  // For the simple format here, locale shouldn't matter
  return dateTime.setLocale('en-US').toFormat('HH:mm')
}

export function format24HourTimeWithSeconds(dateTime: DateTime) {
  return dateTime.toLocaleString(DateTime.TIME_24_WITH_SECONDS)
}

export function formatDate(dateTime: DateTime) {
  return dateTime.toLocaleString(DateTime.DATE_MED)
}

export function formatDateTimeOrEmpty(dateTime: DateTime | string) {
  return dateTime ? formatDateTime(dateTime) : ''
}

export function formatDateTime(dateTime: DateTime | string) {
  return toLocaleDate(dateTime).toLocaleString(DateTime.DATETIME_MED)
}

export function atMidnight(dateTime: DateTime) {
  return dateTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
}

export const withWeekday = (dateTime: DateTime) => {
  return dateTime.setLocale(userLanguage()).toLocaleString(DateTime.DATE_HUGE)
}

export const withWeekDayWithoutYear = (dateTime: DateTime) => {
  try {
    return dateTime.setLocale(userLanguage()).toFormat('EEEE d. MMMM')
  } catch (err) {
    console.error(`Cannot parse dateTime. Locale is - ${userLanguage()}, dateTime is - ${dateTime}`, err)
    return dateTime.setLocale(fallbackLanguage).toFormat('EEEE d. MMMM')
  }
}

export function withWeekdayAndTime(dateTime: DateTime) {
  return `${format24HourTime(dateTime)} ${withWeekday(dateTime)}`
}

export function withTimeAndDate(dateTime: DateTime) {
  return dateTime.setLocale(userLanguage()).toLocaleString(DateTime.DATETIME_SHORT)
}

export function formatDateTimeShortPreserveNull(dateTime: DateTime | string | null): string | null {
  return toLocaleDatePreserveNull(dateTime)?.setLocale(userLanguage())?.toLocaleString(DateTime.DATETIME_SHORT) ?? null
}

export function formatDateTimeTechnical(dateTime: DateTime | string) {
  return toLocaleDate(dateTime).toFormat('yyyy-MM-dd HH:mm')
}

export const dateTimeRendererTechnical = (value: DateTime | string | null | undefined) =>
  toLocaleDatePreserveNull(value)?.toFormat('yyyy-MM-dd HH:mm:ss') ?? ''

export const sameDay = (dateTime1: DateTime, dateTime2: DateTime) =>
  dateTime1.year === dateTime2.year && dateTime1.month === dateTime2.month && dateTime1.day === dateTime2.day

export function isToday(date: DateTime) {
  const now = DateTime.local()
  return sameDay(now, date)
}

export function isYesterday(date: DateTime) {
  const yesterday = DateTime.local().minus({ days: 1 })
  return date.hasSame(yesterday, 'year') && date.hasSame(yesterday, 'month') && date.hasSame(yesterday, 'day')
}

export function isTomorrow(date: DateTime) {
  const tomorrow = DateTime.local().plus({ days: 1 })
  return date.hasSame(tomorrow, 'year') && date.hasSame(tomorrow, 'month') && date.hasSame(tomorrow, 'day')
}

export function formatDateRelativeWithLocale(date: DateTime) {
  if (isToday(date)) return i18next.t('date.today')
  else if (isYesterday(date)) return i18next.t('date.yesterday')
  else if (isTomorrow(date)) return i18next.t('date.tomorrow')
  else return formatDateShortPreserveNull(date)
}

export function getServiceTimeWindow(consignment: Map<string, any>) {
  if (!(consignment && consignment.get('deliveryTimeLatest') && consignment.get('deliveryTimeEarliest'))) return '-'
  else
    return `${format24HourTime(toLocaleDate(consignment.get('deliveryTimeEarliest')))} - ${format24HourTime(
      toLocaleDate(consignment.get('deliveryTimeLatest'))
    )}`
}

export function getDeliveryTimeEstimatedPeriod(
  original: string | DateTime | null,
  adjusted: string | DateTime | null,
  halfTimeWindow: number
) {
  const org = toLocaleDate(original)
  const adj = adjusted ? toLocaleDate(adjusted) : null

  const useableDateTime = shouldUseOriginalTime(org, adj) || adj === null ? org : adj
  const useableRounded = useableDateTime.set({ minute: Math.round(useableDateTime.minute / 10) * 10 })

  const fromTime = calculateRecipientFromTime(useableRounded, halfTimeWindow)
  const toTime = calculateRecipientToTime(useableRounded, halfTimeWindow)
  return `${format24HourTime(fromTime)} - ${format24HourTime(toTime)}`
}

export function diffInMinutesBetweenTwoDates(original: string | undefined, adjusted: string | undefined) {
  if (!original || !adjusted) return 0
  const adjustedLocalDate = toLocaleDate(adjusted)
  const originalLocalDate = toLocaleDate(original)
  const diff = originalLocalDate.diff(adjustedLocalDate, 'minutes').as('minutes')
  return Math.round(diff)
}

function shouldUseOriginalTime(original: DateTime, adjusted: DateTime | null) {
  if (!adjusted) return true
  const diff = original.diff(adjusted, 'minutes').as('minutes')
  return diff >= -20 && diff <= 20
}

function calculateRecipientFromTime(date: DateTime, halfTimeWindow: number) {
  return date.minus({ minute: halfTimeWindow })
}

function calculateRecipientToTime(date: DateTime, halfTimeWindow: number) {
  return date.plus({ minute: halfTimeWindow })
}

export function getTimeZoneFromCountry(country: string) {
  switch (country) {
    case 'NO':
      return 'Europe/Oslo'
    case 'SE':
      return 'Europe/Stockholm'
    case 'DK':
      return 'Europe/Copenhagen'
    case 'FI':
      return 'Europe/Helsinki'
    case 'UK':
    case 'IS':
      return 'Europe/London'
    default:
      return 'Europe/Oslo'
  }
}

export const formatMillisecsToHHMMSS = (duration: number) => Duration.fromMillis(duration).toFormat('hh:mm:ss')

export const formatSecsToHHMM = (duration: number) =>
  Duration.fromMillis(Math.round(duration / 60) * 60 * 1000).toFormat('hh:mm')

export const timeToStringWithZeroPadding = (time: DateTime) =>
  hourMinuteToStringWithZeroPadding(time.get('hour'), time.get('minute'))

export const hourMinuteToStringWithZeroPadding = (hour: number | undefined, minute: number | undefined) =>
  `${padStart(toString(hour), 2, '0')}:${padStart(toString(minute), 2, '0')}`

export const getTimeIfMatchesOtherDate = (
  dateTimeString: ISODateTimeString | undefined,
  dateTime: DateTime,
  zone: string = zoneUTC
): string | boolean | undefined =>
  dateTimeString &&
  sameDay(DateTime.fromISO(dateTimeString, { zone }), dateTime) &&
  format24HourTime(DateTime.fromISO(dateTimeString))

export const parseDateTime = (dateOnly: ISODateString, timeWithOffset: ISOTimeString): DateTime =>
  DateTime.fromISO(dateOnly + 'T' + timeWithOffset)

export interface Time {
  hours: number
  minutes: number
}

export const getHoursMinutes = (time: ISOTimeString): Time => {
  const hourMinutes = time.split(':')
  return {
    hours: Number(hourMinutes[0]),
    minutes: Number(hourMinutes[1])
  }
}

export const pickDate = (dateTime?: ISODateTimeString): ISODateString | undefined => {
  const parsed = toLocaleDatePreserveNull(dateTime)
  return parsed ? (atMidnight(parsed).toISODate() ?? undefined) : undefined
}

export const pickTime = (dateTime?: ISODateTimeString): ISOTimeString | undefined => {
  const parsed = toLocaleDatePreserveNull(dateTime)
  return parsed ? parsed.toLocaleString(DateTime.TIME_24_SIMPLE) : undefined
}

export const timeMinusHours = (time: DateTime, hours: number): DateTime => time.minus({ hours })

export const isWithinServiceTimeWindow = (
  servicePickupTimeFrom: DateTime,
  servicePickupTimeTo: DateTime,
  pickupTime: DateTime
) => servicePickupTimeFrom <= pickupTime && pickupTime <= servicePickupTimeTo

export const firstTimeIsBeforeSecondTime = (pickupTime: DateTime, pickupTimeFrom: DateTime) =>
  pickupTime < pickupTimeFrom
export const isWeekendDay = (date: DateTime) => [6, 7].includes(date.weekday)
export const dateInTheFuture = (date: ISODateString) => {
  const pickedDate = toLocaleDate(date)
  const dateNow = DateTime.local()

  return pickedDate.startOf('day') > dateNow.startOf('day')
}

export function isDate(value: unknown): value is Date {
  return value instanceof Date && !isNaN(value.valueOf())
}

export const isDayInThePast = (date: Date) => DateTime.fromJSDate(date).startOf('day') < DateTime.now().startOf('day')

export const isDayInTheFuture = (date: Date) => DateTime.fromJSDate(date).startOf('day') > DateTime.now().startOf('day')

export const getDiffMinutes = (first: DateTime, second: DateTime) =>
  Math.round(
    first
      .set({ second: 0 })
      .diff(second.set({ second: 0 }), 'minutes')
      .as('minutes')
  )

export const todayISODateString = (): ISODateString => <string>today().toISODate()

export const dateAsISODateOrToday = (date: string | null | undefined | DateTime): ISODateString => {
  const parsedDate = toLocaleDatePreserveNull(date)
  return (parsedDate ? (parsedDate.toISODate() as ISODateString) : todayISODateString()) as ISODateString
}
